'use client';

import { FormEvent, useId, useRef, useState } from 'react';

import type HCaptcha from '@hcaptcha/react-hcaptcha';
import { useRouter } from 'next/navigation';
import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { LazyHCaptcha } from '@/components/functional/LazyHCaptcha';
import {
  DEFAULT_INLINE_CONTACT_TOPIC,
  INLINE_CONTACT_TOPICS,
  type InlineContactTopic,
} from '@/constants/inline-contact';
import { useSendEmail } from '@/hooks/use-send-email';
import { ButtonArrow } from '@/rsc/ui/ButtonArrow';
import { Link as NextLink } from '@/rsc/ui/Link.client';
import { trackEvent } from '@/utils/analytics';
import { IS_CAPTCHA_ENABLED } from '@/utils/captcha';
import { appendFormMessage } from '@/utils/helper-functions/user-form';
import { getParsedUTMParams } from '@/utils/marketing';
// Direct path, not the @/utils/models barrel (generated GraphQL + nav SVGs).
import { GeneralPath } from '@/utils/models/page-paths';

import { ToastRegion, showToast } from './Toast.client';

export type InlineContactFormLabels = {
  topicLabel: string;
  topics: { value: InlineContactTopic; label: string }[];
  contact: string;
  contactPlaceholder: string;
  notice: string;
  privacyLinkLabel: string;
  noticeAfter: string;
  submit: string;
  formError: string;
  formErrorCaptcha: string;
  errors: { contact: string };
};

type Props = {
  labels: InlineContactFormLabels;
  locale: string;
  /** Lands in the mail subject ("Homepage - <purpose> · <topic> - <contact>"). */
  purpose: 'blog_inline' | 'glossary_inline';
  privacyPolicyHref: string;
};

type Values = { topic: InlineContactTopic; contact: string };
type Errors = Partial<Record<keyof Values, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Same digit count as the /contact form's phone rule; an optional leading + is kept.
const PHONE_PATTERN = /^\+?\d{9,20}$/;

/** One field takes either: anything with an @ is an e-mail, everything else a phone number. */
const parseContact = (raw: string): { email: string } | { phoneNumber: string } | null => {
  const value = raw.trim();
  if (value.includes('@')) return EMAIL_PATTERN.test(value) && value.length <= 250 ? { email: value } : null;
  const phone = value.replace(/[\s().-]/g, '');
  return PHONE_PATTERN.test(phone) ? { phoneNumber: phone } : null;
};

// Hand-rolled (as in NewsletterForm) so zod + react-hook-form stay off the article bundle.
const validate = (values: Values, labels: InlineContactFormLabels): Errors => {
  const errors: Errors = {};
  if (!parseContact(values.contact)) errors.contact = labels.errors.contact;
  return errors;
};

const labelClass = css({ display: 'block', fontSize: 'sm', fontWeight: 'medium', mb: 3, color: 'whiteAlpha.700' });

// Topic pill: a visually hidden radio + its label.
const topicClass = css({
  display: 'flex',
  alignItems: 'center',
  minH: '48px',
  px: 4,
  py: 2,
  fontSize: 'sm',
  fontWeight: 'medium',
  lineHeight: '5',
  color: 'whiteAlpha.800',
  bg: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.12)',
  cursor: 'pointer',
  transition: 'border-color 0.2s, background-color 0.2s, color 0.2s, box-shadow 0.2s',
  _hover: { borderColor: 'rgba(255,255,255,0.3)' },
  // Outline, not box-shadow: the checked glow below is a box-shadow and must not swallow the focus ring.
  'input:focus-visible + &': { outline: '2px solid rgba(217,252,89,0.6)', outlineOffset: '2px' },
  // Selected = the card's purple glow on the border (not the lime fill — that's the submit button's).
  'input:checked + &': {
    color: 'text.500',
    bg: 'rgba(147,51,234,0.12)',
    borderColor: 'rgba(168,85,247,0.85)',
    boxShadow: '0 0 0 1px rgba(168,85,247,0.5), 0 0 24px rgba(147,51,234,0.45)',
  },
});

// NewsletterForm's dark input.
const inputClass = css({
  display: 'block',
  width: '100%',
  minWidth: 0,
  outline: 0,
  appearance: 'none',
  fontSize: 'md',
  border: '1px solid',
  borderColor: 'rgba(255,255,255,0.12)',
  borderRadius: '0',
  color: 'text.500',
  backgroundColor: 'rgba(255,255,255,0.05)',
  paddingX: '16px',
  paddingY: '12px',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  _placeholder: { color: 'whiteAlpha.400' },
  _hover: { borderColor: 'rgba(255,255,255,0.25)' },
  _focus: { borderColor: 'rgba(217,252,89,0.6)', boxShadow: '0 0 0 1px rgba(217,252,89,0.4)' },
  '&[aria-invalid=true]': { borderColor: 'red.300', boxShadow: 'none' },
});

const errorClass = css({ display: 'block', mt: 1, fontSize: 'xs', lineHeight: '4', color: 'red.300' });

const visuallyHiddenClass = css({
  border: '0',
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  width: '1px',
  margin: '-1px',
  padding: '0',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'absolute',
});

// Same sizes as NewsletterForm's input + button row on the same page (md on phones, hero lg from sm);
// on phones the button keeps its own width instead of stretching under the field.
const submitClass = cx(
  button({ variant: 'primary', tone: 'dark', size: { base: 'md', sm: 'lg' } }),
  css({ flexShrink: 0, alignSelf: { base: 'flex-start', sm: 'auto' } }),
);

const spinnerClass = css({
  display: 'inline-block',
  borderColor: 'currentColor',
  borderStyle: 'solid',
  borderRadius: '99999px',
  borderWidth: '2px',
  borderBottomColor: 'transparent',
  borderInlineStartColor: 'transparent',
  width: '1em',
  height: '1em',
  animation: 'sh-more-spin 0.45s linear infinite',
});

/**
 * Two-step lead form for the article / glossary mid-content slot: pick a topic (mostly AI), leave an
 * e-mail or a phone number (a topic is pre-selected). No consent checkbox: answering an inquiry the visitor asked for needs no
 * consent (GDPR art. 6(1)(b)/(f)), only the art. 13 notice + privacy-policy link under the field. These
 * leads must not go to marketing lists without a separate opt-in. Posts to the same mail API as the /contact form
 * (UTMs + submit URL appended to the message) and, like it, redirects to /thanks so the existing
 * conversion tracking counts it.
 */
export const InlineContactForm = ({ labels, locale, purpose, privacyPolicyHref }: Props) => {
  const router = useRouter();
  const id = useId();
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [values, setValues] = useState<Values>({ topic: DEFAULT_INLINE_CONTACT_TOPIC, contact: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutate, isLoading } = useSendEmail();

  const update = <K extends keyof Values>(key: K, value: Values[K]) => {
    const next = { ...values, [key]: value };
    setValues(next);
    // Errors appear after the first submit attempt, then clear live as the user fixes them.
    if (isSubmitted) setErrors(validate(next, labels));
  };

  const resetCaptcha = () => {
    setCaptchaToken('');
    captchaRef.current?.resetCaptcha();
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);
    const nextErrors = validate(values, labels);
    setErrors(nextErrors);
    const contact = parseContact(values.contact);
    if (Object.keys(nextErrors).length > 0 || !contact) return;

    let token = captchaToken;
    if (IS_CAPTCHA_ENABLED && !token) {
      const captchaResult = await captchaRef.current?.execute({ async: true });
      if (!captchaResult?.response) {
        resetCaptcha();
        showToast({ status: 'error', title: labels.formErrorCaptcha });
        return;
      }
      token = captchaResult.response;
    }

    const topic = values.topic;
    const topicLabel = INLINE_CONTACT_TOPICS[topic];
    const utmParams = getParsedUTMParams();
    const message = `Topic: ${topicLabel}`;
    const formData = new FormData();
    formData.append('purpose', `${purpose} · ${topicLabel}`);
    // No name field: the contact itself goes into the mail subject's name slot.
    formData.append('name', 'email' in contact ? contact.email : contact.phoneNumber);
    if ('email' in contact) formData.append('email', contact.email);
    else formData.append('phoneNumber', contact.phoneNumber);
    formData.append('message', utmParams ? appendFormMessage(message, utmParams) : message);
    formData.append('captchaToken', token);

    mutate(formData, {
      onSuccess: () => {
        trackEvent({ event: 'contact_form_submit', form_id: 'inline', form_locale: locale, purpose: topic });
        router.push(GeneralPath.THANKS);
      },
      onError: () => {
        resetCaptcha();
        showToast({ status: 'error', title: labels.formError });
      },
    });
  };

  const fieldId = (name: string) => `${id}-${name}`;
  const fieldError = (name: keyof Values) =>
    errors[name] ? (
      <span id={fieldId(`${name}-error`)} className={errorClass}>
        {errors[name]}
      </span>
    ) : null;

  return (
    <>
      <form
        className={css({ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative', zIndex: 1 })}
        onSubmit={onSubmit}
        onFocusCapture={() => setHasInteracted(true)}
        noValidate
      >
        <fieldset className={css({ border: 0, m: 0, p: 0, minW: 0 })}>
          <legend className={cx(labelClass, css({ p: 0 }))}>{labels.topicLabel}</legend>
          <div className={css({ display: 'grid', gridTemplateColumns: { base: '1fr', sm: '1fr 1fr' }, gap: 2 })}>
            {labels.topics.map((topic) => (
              <div key={topic.value} className={css({ position: 'relative' })}>
                <input
                  id={fieldId(`topic-${topic.value}`)}
                  type="radio"
                  name="topic"
                  value={topic.value}
                  checked={values.topic === topic.value}
                  onChange={() => !isLoading && update('topic', topic.value)}
                  className={visuallyHiddenClass}
                />
                <label htmlFor={fieldId(`topic-${topic.value}`)} className={topicClass}>
                  {topic.label}
                </label>
              </div>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor={fieldId('contact')} className={labelClass}>
            {labels.contact}
          </label>
          <div className={css({ display: 'flex', flexDirection: { base: 'column', sm: 'row' }, gap: 3 })}>
            <input
              id={fieldId('contact')}
              name="contact"
              type="text"
              inputMode="email"
              autoComplete="email"
              required
              placeholder={labels.contactPlaceholder}
              value={values.contact}
              onChange={(event) => update('contact', event.target.value)}
              readOnly={isLoading}
              className={inputClass}
              aria-invalid={!!errors.contact}
              aria-describedby={errors.contact ? fieldId('contact-error') : undefined}
            />
            <button
              type="submit"
              className={submitClass}
              disabled={isLoading}
              data-loading={isLoading ? '' : undefined}
            >
              {isLoading ? (
                <span
                  className={css({ display: 'flex', position: 'absolute', inset: 0, m: 'auto', w: '1em', h: '1em' })}
                >
                  <span className={spinnerClass} />
                </span>
              ) : null}
              <span style={isLoading ? { opacity: 0 } : undefined}>{labels.submit}</span>
              <ButtonArrow />
            </button>
          </div>
          {fieldError('contact')}
        </div>
        <span className={css({ display: 'block', fontSize: 'xs', lineHeight: '4', color: 'whiteAlpha.600' })}>
          {labels.notice}{' '}
          <NextLink
            target="_blank"
            href={privacyPolicyHref}
            prefetch={false}
            className={css({ color: 'whiteAlpha.900', textDecoration: 'underline', _hover: { color: 'white' } })}
          >
            {labels.privacyLinkLabel}
          </NextLink>
          {labels.noticeAfter}
        </span>
        {IS_CAPTCHA_ENABLED && hasInteracted && (
          <LazyHCaptcha
            ref={captchaRef}
            sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY as string}
            onVerify={setCaptchaToken}
          />
        )}
      </form>
      <ToastRegion />
    </>
  );
};
